import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { slice, uniqueId } from 'lodash';
import Icon from '../Icon/Icon';
import formatTimeOfDay from './utils/formatTimeOfDay';
import AllOrOne from '../AllOrOne/AllOrOne';
import { DAYS_OF_THE_WEEK, CLOSED_DAY } from './constants';

const ABBREVIATED_DAYS = {
  monday: 'mon',
  tuesday: 'tue',
  wednesday: 'wed',
  thursday: 'thu',
  friday: 'fri',
  saturday: 'sat',
  sunday: 'sun',
};

const getHours = (dayOfWeek, hours = [], closedText, startTimeKey, endTimeKey) => {
  const openDay = hours[dayOfWeek];

  let openClose = <div className="mb-0.5">{closedText}</div>;

  if (openDay !== undefined) {
    openClose = openDay.map(v => (
      <div
        id={uniqueId(`${dayOfWeek}-hours-`)}
        key={uniqueId(dayOfWeek)}
      >
        {formatTimeOfDay(v[startTimeKey])} - {formatTimeOfDay(v[endTimeKey])}
      </div>
      ),
    );
  }

  return openClose;
};

const HoursOfOperationToday = ({
  className = '',
  hours = {},
  showAll = true,
  showToggle = false,
  shouldStartWeekToday = true,
  abbreviateDays = true,
  closedText = CLOSED_DAY,
  startTimeKey = 'opens',
  endTimeKey = 'closes',
}) => {
  const todayIndex = shouldStartWeekToday ? moment().day() : 1; // today or monday
  const weekFromToday = [
    ...slice(DAYS_OF_THE_WEEK, todayIndex),
    ...slice(DAYS_OF_THE_WEEK, 0, todayIndex),
  ];

  const [showAllHours, setshowAllHours] = useState(false);

  const hoursToggleClass = (isFirst) => classNames({
    'flex items-start': true,
    'font-bold': isFirst && !showToggle,
    'first:text-action-blue': isFirst && showToggle,
    'group-hover:text-deep-blue': isFirst && showToggle,
  });

  const hoursDayLabel = () => classNames({
      'mr-4 capitalize inline-block': true,
      'w-auto': !showAllHours && showToggle,
    },
    abbreviateDays ? 'w-8' : 'w-20',
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setshowAllHours(!showAllHours);
    }
  };

  return (
    <div
      className={`${className} text-sm text-text-blue group`}
      data-test-element="hours-of-operation"
      aria-label={showAllHours ? 'Organization hours expanded' : 'Organization hours today. Click to expand for hours on other days'}
      aria-live="polite"
      aria-expanded={showAllHours}
    >
      <AllOrOne
        showAll={showToggle ? (showAll && showAllHours) : showAll}
      >        
        {
          weekFromToday.map((dayOfWeek, index) => (
            <div
              className={hoursToggleClass(index === 0)}
              key={uniqueId(dayOfWeek)}
              onClick={showToggle ? () => setshowAllHours(!showAllHours) : null}
              onKeyDown={handleKeyDown}
              {...showToggle ? { role: 'button' } : null}
              {...showToggle ? { tabIndex: '0'} : null}
            >
              <div className={hoursDayLabel()}>
                <div id={uniqueId(dayOfWeek)}>
                  {abbreviateDays ? ABBREVIATED_DAYS[dayOfWeek] : dayOfWeek}
                </div>
              </div>
  
              <div className="inline-block">
                {getHours(dayOfWeek, hours, closedText, startTimeKey, endTimeKey)}
              </div>
              
              {showToggle && showAll && DAYS_OF_THE_WEEK[todayIndex] === dayOfWeek &&
                <span className="ml-2 my-auto inline-flex">
                  <Icon
                    color="action-blue" // should always match link-color (tw: action-blue)
                    icon="ChevronArrow"
                    direction={showAllHours ? 'up' : 'down'}
                  />
                </span>
              }
            </div>
          ))
        }
      </AllOrOne>
    </div>
  );
};

HoursOfOperationToday.propTypes = {
  /** hours of operation values to display */
  hours: PropTypes.object,
  /** should display all days or only the first one */
  showAll: PropTypes.bool,
  /** should display a separate caret to individually toggle hours */
  showToggle: PropTypes.bool,
  /** should start the days of the week on current day */
  shouldStartWeekToday: PropTypes.bool,
  /** show abbreviated day labels instead of full day (i.e. `Mon` vs `Monday`) */
  abbreviateDays: PropTypes.bool,
  /** text to display when given day has no hours associated */
  closedText: PropTypes.string,
  /** key for start time in hours object (`{ monday: { opens: '09:00', closes: '17:00' } }`) */
  startTimeKey: PropTypes.string,
  /** key for end time in hours object (`{ monday: { opens: '09:00', closes: '17:00' } }`) */
  endTimeKey: PropTypes.string,
};

export default HoursOfOperationToday;
